import { matterDetailQueryKey } from "@/lib/data/static/matter-query-key";
import {
  invalidateSearchQueries,
  isCanonicalSearchQuery,
} from "@/lib/data/react-query/search/invalidation";
import { queryKeys } from "@/lib/data/static/query-keys";
import {
  type APIError,
  type APIResponse,
  type Matter,
  type MatterExpense,
} from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";
import {
  createMatterExpense,
  deleteMatterExpense,
  updateMatterExpense,
} from "../../requests/matters/matter-expenses";

const getMatterExpenses = (matter: Matter) =>
  Array.isArray(matter.expenses) ? matter.expenses : [];

const setMatterExpenses = (
  matter: Matter,
  expenses: Matter["expenses"],
): Matter => ({
  ...matter,
  expenses,
});

const mergeMatterExpense = (
  expense: MatterExpense,
  changes: Partial<MatterExpense>,
): MatterExpense => {
  const definedChanges = Object.fromEntries(Object.entries(changes));

  return { ...expense, ...definedChanges } as MatterExpense;
};

export const useCreateMatterExpense = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMatterExpense>[0]) =>
      createMatterExpense(data),
    onMutate: (data) => {
      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (_error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toast.error("Error creating expense");
    },
    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      toast.success("Expense created successfully");
    },
  });
};

export const useUpdateMatterExpense = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMatterExpense>[0]) =>
      updateMatterExpense(data),
    onMutate: (data) => {
      const previousData = queryClient.getQueryData(
        matterDetailQueryKey(data.matterId),
      );
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: setMatterExpenses(
              oldData.data,
              getMatterExpenses(oldData.data).map((expense) =>
                expense.id === data.id
                  ? mergeMatterExpense(expense, data)
                  : expense,
              ),
            ),
          };
        },
      );
      return {
        previousData,
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error updating expense");
    },
    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      toast.success("Expense updated successfully");
    },
  });
};

export const useDeleteMatterExpense = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMatterExpense>[0]) =>
      deleteMatterExpense(data),
    onMutate: (data) => {
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: setMatterExpenses(
              oldData.data,
              getMatterExpenses(oldData.data).filter(
                (expense) => expense.id !== data.id,
              ),
            ),
          };
        },
      );

      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error deleting expense");
    },
    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      toast.success("Expense deleted successfully");
    },
  });
};
